import { IPicker } from '@rbi-ctg/menu';
import {
  IProductWizardFlowSelections,
  UserSelectionPickerAspect,
} from 'state/product-wizard-v2/types';
import { IPricingFunctionFn } from 'types/menu-pricing';
import { isCombo, isItem } from 'utils/menu';

interface IMatchPickerAspectParams {
  pickerData: IPicker;
  pickerAspectOptionIdentifier: string;
  selectedPickerAspects: Record<string, string>;
  currentAspectId?: string;
}

interface IFindCheapestPickerOptionParams {
  pickerData: IPicker;
  pickerAspectOptionIdentifier: string;
  pricingFunction: IPricingFunctionFn;
  productWizardFlowUserSelections?: IProductWizardFlowSelections[];
}

interface IGetPickerOptionPriceFromSelectionsParams {
  pickerData: IPicker;
  pickerAspectOptionIdentifier: string;
  pricingFunction: IPricingFunctionFn;
  productWizardFlowUserSelections?: UserSelectionPickerAspect | undefined;
  currentPickerAspectId?: string;
}

export const findMatchingPickerOption = ({
  pickerData,
  pickerAspectOptionIdentifier,
  selectedPickerAspects,
  currentAspectId,
}: IMatchPickerAspectParams) => {
  // First try to find a match considering all selections
  if (Object.keys(selectedPickerAspects).length > 0) {
    // Get all selected picker aspects except the current one
    const otherUserSelectedPickerAspects = Object.entries(selectedPickerAspects)
      .filter(([aspectId]) => aspectId !== currentAspectId)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Find the option that matches all our picker aspect selections
    const matchingOption = pickerData.options.find(option => {
      const mappings = option.pickerItemMappings;

      // Check if this option has the current picker aspect value we're looking for
      const hasCurrentAspectValue = mappings.some(
        mapping => mapping.pickerAspectValueIdentifier === pickerAspectOptionIdentifier
      );

      if (!hasCurrentAspectValue) {
        return false;
      }

      // Check if all other selected aspects match this option's mappings
      return Object.entries(otherUserSelectedPickerAspects).every(([aspectId, aspectValue]) => {
        return mappings.some(
          mapping =>
            mapping.pickerAspect._id === aspectId &&
            mapping.pickerAspectValueIdentifier === aspectValue
        );
      });
    });

    if (matchingOption) {
      return matchingOption;
    }
  }

  // Fallback to simpler matching if no selections or no match found
  return pickerData.options.find(option =>
    option.pickerItemMappings.some(
      mapping => mapping.pickerAspectValueIdentifier === pickerAspectOptionIdentifier
    )
  );
};

/**
 * Given a picker aspect option identifier, go through the picker data to find options that partially match the
 * picker aspect option identifier. For each of these options, check if the option is an item or combo and then
 * apply the pricing function to get the price. Return the cheapest price.
 *
 * @returns {number} The cheapest price for the picker aspect option identifier.
 */
export const findCheapestPickerOption = ({
  pickerData,
  pickerAspectOptionIdentifier,
  pricingFunction,
}: IFindCheapestPickerOptionParams) => {
  // Find all options that match our picker aspect identifier
  const optionsForPickerAspectOptionIdentifier = pickerData.options.filter(option =>
    option.pickerItemMappings.some(
      mapping => mapping.pickerAspectValueIdentifier === pickerAspectOptionIdentifier
    )
  );

  const cheapestPrice = optionsForPickerAspectOptionIdentifier
    .filter(option => isCombo(option.option) || isItem(option.option))
    .reduce((cheapest, option) => {
      const price = pricingFunction({ item: option.option });
      return Math.min(cheapest, price);
    }, Infinity);

  return cheapestPrice;
};

export const getPickerOptionPriceFromSelections = ({
  pickerData,
  currentPickerAspectId,
  pickerAspectOptionIdentifier,
  pricingFunction,
  productWizardFlowUserSelections,
}: IGetPickerOptionPriceFromSelectionsParams) => {
  const selectedPickerAspects = productWizardFlowUserSelections ?? {};

  // Find picker aspects that come before the current one
  const priorPickerAspects = pickerData.pickerAspects.slice(
    0,
    pickerData.pickerAspects.findIndex(aspect => aspect._id === currentPickerAspectId)
  );

  // Get user selections for prior picker aspects only
  const priorSelections = Object.entries(selectedPickerAspects)
    .filter(([aspectId]) => priorPickerAspects.some(aspect => aspect._id === aspectId))
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  // Calculate baseline price using prior selections
  const baselinePrice = pickerData.options
    .filter(option => {
      // Filter options that match prior selections
      const matchesPriorSelections = Object.entries(priorSelections).every(([aspectId, value]) => {
        return option.pickerItemMappings.some(
          mapping =>
            mapping.pickerAspect._id === aspectId && mapping.pickerAspectValueIdentifier === value
        );
      });
      return matchesPriorSelections && (isCombo(option.option) || isItem(option.option));
    })
    .reduce((cheapest, option) => {
      const price = pricingFunction({ item: option.option });
      return Math.min(cheapest, price);
    }, Infinity);

  // Find matching option based on current selection and previous selections
  const matchingOption = findMatchingPickerOption({
    pickerData,
    pickerAspectOptionIdentifier,
    selectedPickerAspects,
    currentAspectId: currentPickerAspectId,
  });

  if (!matchingOption || !(isCombo(matchingOption.option) || isItem(matchingOption.option))) {
    return 0;
  }

  // Calculate price for the matching option
  const optionPrice = pricingFunction({ item: matchingOption.option });

  // Return the upcharge (difference between option price and baseline)
  // If the difference is negative, return 0 (we don't show downcharges)
  return Math.max(0, optionPrice - baselinePrice);
};
